import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import './StaticPage.css';

const pageContent = {
  'about': {
    title: 'About Us',
    content: `
      <h2>Welcome to Our Store</h2>
      <p>We are a leading online retailer committed to providing high-quality products and exceptional customer service.</p>

      <h3>Our Mission</h3>
      <p>Our mission is to deliver the best shopping experience by offering a wide selection of products at competitive prices, backed by outstanding customer support.</p>

      <h3>Our Values</h3>
      <ul>
        <li><strong>Quality:</strong> We carefully select every product to ensure it meets our high standards.</li>
        <li><strong>Customer Service:</strong> Your satisfaction is our top priority.</li>
        <li><strong>Innovation:</strong> We continuously improve our platform to enhance your shopping experience.</li>
        <li><strong>Integrity:</strong> We conduct our business with honesty and transparency.</li>
      </ul>

      <h3>Why Shop With Us?</h3>
      <ul>
        <li>Wide selection of quality products</li>
        <li>Competitive pricing</li>
        <li>Fast and reliable shipping</li>
        <li>Secure payment processing</li>
        <li>Easy returns and exchanges</li>
        <li>Dedicated customer support team</li>
      </ul>
    `
  },
  'contact': {
    title: 'Contact Us',
    content: `
      <h2>Get In Touch</h2>
      <p>We'd love to hear from you! If you have any questions, concerns, or feedback, please don't hesitate to reach out.</p>

      <h3>Customer Service</h3>
      <p><strong>Email:</strong> support@example.com</p>
      <p><strong>Phone:</strong> 1-800-123-4567</p>
      <p><strong>Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST</p>

      <h3>Business Inquiries</h3>
      <p><strong>Email:</strong> business@example.com</p>

      <h3>Mailing Address</h3>
      <p>
        123 Commerce Street<br>
        Suite 100<br>
        New York, NY 10001<br>
        United States
      </p>

      <h3>Follow Us</h3>
      <p>Stay connected with us on social media for the latest updates, promotions, and more!</p>
    `
  },
  'privacy-policy': {
    title: 'Privacy Policy',
    content: `
      <p><em>Last updated: January 2025</em></p>

      <h2>Introduction</h2>
      <p>This Privacy Policy describes how we collect, use, and protect your personal information when you visit our website or make a purchase.</p>

      <h3>Information We Collect</h3>
      <p>We collect information that you provide directly to us, including:</p>
      <ul>
        <li>Name and contact information</li>
        <li>Billing and shipping addresses</li>
        <li>Payment information</li>
        <li>Order history</li>
        <li>Account credentials</li>
      </ul>

      <h3>How We Use Your Information</h3>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Process and fulfill your orders</li>
        <li>Communicate with you about your orders and account</li>
        <li>Send you marketing communications (with your consent)</li>
        <li>Improve our website and services</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h3>Data Security</h3>
      <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>

      <h3>Your Rights</h3>
      <p>You have the right to access, correct, or delete your personal information. You may also opt out of marketing communications at any time.</p>

      <h3>Contact Us</h3>
      <p>If you have any questions about this Privacy Policy, please contact us at privacy@example.com.</p>
    `
  },
  'terms-of-service': {
    title: 'Terms of Service',
    content: `
      <p><em>Last updated: January 2025</em></p>

      <h2>Agreement to Terms</h2>
      <p>By accessing or using our website, you agree to be bound by these Terms of Service and all applicable laws and regulations.</p>

      <h3>Use of Website</h3>
      <p>You may use our website for lawful purposes only. You agree not to:</p>
      <ul>
        <li>Violate any laws or regulations</li>
        <li>Infringe on intellectual property rights</li>
        <li>Transmit harmful or malicious code</li>
        <li>Attempt to gain unauthorized access to our systems</li>
      </ul>

      <h3>Product Information</h3>
      <p>We strive to provide accurate product descriptions and pricing. However, we do not warrant that product descriptions or other content is accurate, complete, reliable, or error-free.</p>

      <h3>Orders and Pricing</h3>
      <p>All orders are subject to acceptance and availability. We reserve the right to refuse or cancel any order for any reason.</p>

      <h3>Limitation of Liability</h3>
      <p>To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or products.</p>

      <h3>Changes to Terms</h3>
      <p>We reserve the right to modify these Terms of Service at any time. Your continued use of the website after changes constitutes acceptance of the modified terms.</p>
    `
  },
  'shipping-delivery': {
    title: 'Shipping & Delivery',
    content: `
      <h2>Shipping Information</h2>
      <p>We offer fast and reliable shipping to ensure your order arrives safely and on time.</p>

      <h3>Shipping Methods</h3>
      <ul>
        <li><strong>Standard Shipping:</strong> 5-7 business days</li>
        <li><strong>Express Shipping:</strong> 2-3 business days</li>
        <li><strong>Overnight Shipping:</strong> 1 business day</li>
      </ul>

      <h3>Shipping Costs</h3>
      <p>Shipping costs are calculated based on the weight and dimensions of your order, as well as your shipping address. The exact shipping cost will be displayed at checkout.</p>

      <h3>Free Shipping</h3>
      <p>We offer free standard shipping on orders over $50 within the continental United States.</p>

      <h3>International Shipping</h3>
      <p>We currently ship to select international destinations. International shipping times and costs vary by location.</p>

      <h3>Order Processing</h3>
      <p>Orders are typically processed within 1-2 business days. You will receive a confirmation email with tracking information once your order has shipped.</p>

      <h3>Delivery Issues</h3>
      <p>If you experience any issues with your delivery, please contact our customer service team at support@example.com.</p>
    `
  },
  'returns-refunds': {
    title: 'Returns & Refunds',
    content: `
      <h2>Return Policy</h2>
      <p>We want you to be completely satisfied with your purchase. If you're not happy with your order, we're here to help.</p>

      <h3>Return Window</h3>
      <p>You have 30 days from the date of delivery to return most items for a full refund or exchange.</p>

      <h3>Return Conditions</h3>
      <p>To be eligible for a return, items must be:</p>
      <ul>
        <li>In their original condition</li>
        <li>Unworn and unused</li>
        <li>In the original packaging</li>
        <li>With all tags attached</li>
      </ul>

      <h3>How to Return an Item</h3>
      <ol>
        <li>Contact our customer service team at support@example.com to initiate a return</li>
        <li>Receive your return authorization and shipping label</li>
        <li>Pack the item securely in its original packaging</li>
        <li>Ship the item using the provided label</li>
      </ol>

      <h3>Refunds</h3>
      <p>Once we receive and inspect your return, we will process your refund within 5-7 business days. Refunds will be issued to the original payment method.</p>

      <h3>Exchanges</h3>
      <p>If you need a different size or color, we're happy to exchange your item. Contact us to arrange an exchange.</p>

      <h3>Non-Returnable Items</h3>
      <p>The following items cannot be returned:</p>
      <ul>
        <li>Personalized or custom-made products</li>
        <li>Perishable goods</li>
        <li>Intimate or sanitary products</li>
        <li>Sale or clearance items (unless defective)</li>
      </ul>
    `
  }
};

const StaticPage = () => {
  const location = useLocation();
  const page = location.pathname.substring(1); // Remove leading slash
  const { updateBreadcrumbs } = useBreadcrumb();
  const content = pageContent[page];

  useEffect(() => {
    if (content) {
      updateBreadcrumbs([
        { label: 'Home', path: '/' },
        { label: content.title, path: `/${page}` }
      ]);
    }
  }, [page, content, updateBreadcrumbs]);

  if (!content) {
    return (
      <div className="static-page">
        <div className="static-page-container">
          <h1>Page Not Found</h1>
          <p>The page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="static-page">
      <div className="static-page-container">
        <h1>{content.title}</h1>
        <div className="static-page-content" dangerouslySetInnerHTML={{ __html: content.content }} />
      </div>
    </div>
  );
};

export default StaticPage;
